import {
  getPublicationLocales,
  type Locale,
  parseLocale,
} from "@repo/fika-shared/src/locale"
import {
  buildTranslatableField,
  parseTranslatableField,
  translate,
} from "@repo/fika-shared/src/translate"
import { useNavigate, useParams } from "@solidjs/router"
import { createMutation, createQuery } from "@urql/solid"
import { createEffect, createMemo, createSignal, For, on, Show } from "solid-js"
import { uploadFile } from "@/data/upload-file"
import { Box } from "@/designSystem/components/box"
import { Button, LinkButton } from "@/designSystem/components/button"
import { Form } from "@/designSystem/components/form"
import { Table } from "@/designSystem/components/table"
import { TextField } from "@/designSystem/components/text-field"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { Explanation } from "@/designSystem/patterns/explanation"
import { FileUpload } from "@/designSystem/patterns/file-upload"
import { Info } from "@/designSystem/patterns/info"
import { NavigationWithButtons } from "@/designSystem/patterns/navigation"
import { PublicationTabs } from "@/designSystem/patterns/publication-tabs"
import {
  LanguageSelector,
  TranslatableField,
  TranslatableFieldGroup,
} from "@/designSystem/patterns/translatable-field"
import { graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { toast } from "@/lib/toast"
import { createBlogRouter } from "@/routes/posts-router"
import { AudienceForm, type AudienceSettings } from "./_audience-form"
import { SimpleWysiwyg } from "./editor/simple-wysiwyg"
import {
  filesDeleteButtonClass,
  filesTableClass,
  formContainerClass,
  formLinkClass,
} from "./publication-forms.css"

interface PendingFile {
  id: string
  name: string
  size: number | null
  src: string | null
  file: File | null
}

const NEW_FORM_ID = "new"

export function PublicationForms() {
  const params = useParams()

  return (
    <Show when={params.formId} fallback={<PublicationFormsList />}>
      <PublicationFormEditor />
    </Show>
  )
}

function PublicationFormsList() {
  const { LL, locale } = useI18nContext()
  const interfaceLocale = createMemo(() => parseLocale(locale()))
  const { paths } = createBlogRouter()

  const [result, reexecuteQuery] = createQuery({
    query: graphql(`
      query PublicationFormsList {
        me {
          id
          publication {
            id
            leadMagnetForms {
              id
              name
              private
              subscribersCount
              files {
                id
              }
            }
          }
        }
      }
    `),
    requestPolicy: "cache-and-network",
  })

  const [deleteFormState, deleteForm] = createMutation(
    graphql(`
      mutation DeleteLeadMagnetFormFromList($formId: ID!) {
        deleteLeadMagnetForm(formId: $formId) {
          ... on MutationDeleteLeadMagnetFormSuccess {
            data
          }
        }
      }
    `),
  )

  const forms = createMemo(
    () => result.data?.me?.publication?.leadMagnetForms ?? [],
  )

  async function onDelete(formId: string) {
    await deleteForm({ formId })
    reexecuteQuery({ requestPolicy: "network-only" })
    toast.success(LL().blog.formsPage.formDeleted())
  }

  const buttons = createMemo(() => [
    {
      label: LL().blog.title(),
      href: paths().url,
    } as const,
  ])

  return (
    <MainLayout
      hue="amber"
      header={
        <NavigationWithButtons
          hue="amber"
          title={LL().blog.forms()}
          buttons={buttons()}
        >
          <LinkButton hue="amber" href={paths().formsNew()}>
            {LL().blog.formsPage.createForm()}
          </LinkButton>
        </NavigationWithButtons>
      }
    >
      <Box gap="16">
        <PublicationTabs />
        <Box gap="24">
          <Show
            when={!result.fetching}
            fallback={<Info>{LL().common.loading()}</Info>}
          >
            <Show
              when={forms().length > 0}
              fallback={
                <Explanation
                  hue="amber"
                  title={LL().blog.formsPage.blankSlate.title()}
                  illustration="special-growing-leaves"
                >
                  <Explanation.Bullet
                    hue="amber"
                    iconName="users-round"
                    title={LL().blog.formsPage.blankSlate.growReadership()}
                    description={LL().blog.formsPage.blankSlate.growReadershipDescription()}
                  />
                  <Explanation.Bullet
                    hue="amber"
                    iconName="sparkles"
                    title={LL().blog.formsPage.blankSlate.convertReaders()}
                    description={LL().blog.formsPage.blankSlate.convertReadersDescription()}
                  />
                  <Explanation.Bullet
                    hue="amber"
                    iconName="rocket"
                    title={LL().blog.formsPage.blankSlate.goBeyondText()}
                    description={LL().blog.formsPage.blankSlate.goBeyondTextDescription()}
                  />
                  <Box alignItems="flex-start">
                    <LinkButton hue="amber" href={paths().formsNew()}>
                      {LL().blog.formsPage.createForm()}
                    </LinkButton>
                  </Box>
                </Explanation>
              }
            >
              <Table>
                <Table.Head>
                  <Table.Header>{LL().blog.formsPage.name()}</Table.Header>
                  <Table.Header>{LL().blog.formsPage.audience()}</Table.Header>
                  <Table.Header>{LL().blog.formsPage.downloads()}</Table.Header>
                  <Table.Header>
                    {LL().blog.formsPage.subscribers()}
                  </Table.Header>
                  <Table.Header>{LL().blog.formsPage.actions()}</Table.Header>
                </Table.Head>
                <Table.Body>
                  <For each={forms()}>
                    {form => (
                      <Table.Row>
                        <Table.Cell>
                          <a
                            href={paths().formsEdit(form.id)}
                            class={formLinkClass}
                          >
                            {translate(form.name, interfaceLocale()) ??
                              form.name}
                          </a>
                        </Table.Cell>
                        <Table.Cell>
                          {form.private
                            ? LL().blog.formsPage.subscribersOnly()
                            : LL().blog.formsPage.everyone()}
                        </Table.Cell>
                        <Table.Cell>{form.files.length}</Table.Cell>
                        <Table.Cell>{form.subscribersCount}</Table.Cell>
                        <Table.Cell>
                          <Button
                            hue="rose"
                            kind="ghost"
                            onClick={() => onDelete(form.id)}
                            active={deleteFormState.fetching}
                          >
                            {LL().common.delete()}
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </For>
                </Table.Body>
              </Table>
            </Show>
          </Show>
        </Box>
      </Box>
    </MainLayout>
  )
}

function PublicationFormEditor() {
  const { LL, locale } = useI18nContext()
  const { paths } = createBlogRouter()
  const navigate = useNavigate()
  const params = useParams()

  const [result, reexecuteQuery] = createQuery({
    query: graphql(`
      query PublicationForm {
        me {
          id
          publication {
            id
            name
            locale
            additionalLanguages
            leadMagnetForms {
              id
              name
              content
              emailSubject
              emailBody
              private
              files {
                id
                title
                src
              }
            }
          }
        }
      }
    `),
    requestPolicy: "cache-and-network",
  })

  const [, upsertForm] = createMutationWithErrorHandling(
    graphql(
      `mutation UpsertLeadMagnetFormInEditor($input: UpsertLeadMagnetFormInput!) {
        upsertLeadMagnetForm(input: $input) {
          ... on MutationUpsertLeadMagnetFormSuccess {
            data {
              id
            }
          }
          ...ErrorFragment
        }
      }
    `,
      [ErrorFragment],
    ),
    "upsertLeadMagnetForm",
  )

  const userId = createMemo(() => result.data?.me?.id ?? "")
  const publication = createMemo(() => result.data?.me?.publication)
  const form = createMemo(() => {
    if (!params.formId || params.formId === NEW_FORM_ID) return null
    return (
      publication()?.leadMagnetForms.find(item => item.id === params.formId) ??
      null
    )
  })
  const isUnknownFormId = createMemo(() => {
    if (!params.formId || params.formId === NEW_FORM_ID) return false
    if (result.fetching) return false
    return !form()
  })

  const defaultDescription = createMemo(() =>
    createSimpleDoc([LL().blog.formsPage.defaultDescription()]),
  )
  const defaultEmailSubject = createMemo(() => {
    const publicationName = publication()?.name?.trim()
    if (publicationName) {
      return LL().blog.formsPage.defaultEmailSubjectWithName({
        publicationName,
      })
    }
    return LL().blog.formsPage.defaultEmailSubject()
  })
  const defaultEmailBody = createMemo(() => {
    const publicationName = publication()?.name?.trim()
    return createSimpleDoc([
      LL().blog.formsPage.defaultEmailGreeting(),
      LL().blog.formsPage.defaultEmailIntro(),
      LL().blog.formsPage.defaultEmailOutro(),
      publicationName
        ? LL().blog.formsPage.defaultEmailSignatureWithName({ publicationName })
        : LL().blog.formsPage.defaultEmailSignature(),
    ])
  })

  const [name, setName] = createSignal("")
  const [content, setContent] = createSignal(
    form()?.content ?? defaultDescription(),
  )
  const [emailSubject, setEmailSubject] = createSignal(
    form()?.emailSubject ?? defaultEmailSubject(),
  )
  const [emailBody, setEmailBody] = createSignal(
    form()?.emailBody ?? defaultEmailBody(),
  )
  const [audienceSettings, setAudienceSettings] =
    createSignal<AudienceSettings>({
      private: false,
      dirty: false,
    })
  const [files, setFiles] = createSignal<PendingFile[]>([])
  const [isSaving, setIsSaving] = createSignal(false)
  const isEditing = createMemo(() => !!form())

  const publicationLocale = createMemo(() =>
    parseLocale(publication()?.locale ?? locale()),
  )
  const availableLocales = createMemo(() =>
    getPublicationLocales(
      publicationLocale(),
      publication()?.additionalLanguages ?? [],
    ),
  )

  const canSave = createMemo(() => {
    const parsedName = parseTranslatableField(name())
    const parsedEmailSubject = parseTranslatableField(emailSubject())
    const nameValue =
      parsedName?.values[parsedName.primaryLocale] ??
      Object.values(parsedName?.values ?? {})[0] ??
      name()
    const emailSubjectValue =
      parsedEmailSubject?.values[parsedEmailSubject.primaryLocale] ??
      Object.values(parsedEmailSubject?.values ?? {})[0] ??
      emailSubject()

    return nameValue.trim().length > 0 && emailSubjectValue.trim().length > 0
  })

  const filesForTable = createMemo(() =>
    files().map(file => ({
      id: file.id,
      name: file.name,
      size: file.size,
      src: file.src ?? file.name,
    })),
  )

  const audiencePost = createMemo(() => ({
    private: audienceSettings().private,
  }))

  createEffect(
    on(
      [form, defaultDescription, defaultEmailSubject, defaultEmailBody],
      ([
        nextForm,
        nextDefaultDescription,
        nextDefaultEmailSubject,
        nextDefaultEmailBody,
      ]) => {
        setName(nextForm?.name ?? "")
        setContent(nextForm?.content ?? nextDefaultDescription)
        setEmailSubject(nextForm?.emailSubject ?? nextDefaultEmailSubject)
        setEmailBody(nextForm?.emailBody ?? nextDefaultEmailBody)
        setAudienceSettings({
          private: nextForm?.private ?? false,
          dirty: false,
        })
        setFiles(
          (nextForm?.files ?? []).map(file => ({
            id: file.id,
            name: getFileName(file.src),
            size: null,
            src: file.src,
            file: null,
          })),
        )
      },
    ),
  )

  createEffect(() => {
    if (!isUnknownFormId()) return
    navigate(paths().forms(), { replace: true })
  })

  function refetch() {
    reexecuteQuery({ requestPolicy: "network-only" })
  }

  async function onSave() {
    if (!canSave()) return
    if (!userId()) return
    if (isUnknownFormId()) return

    setIsSaving(true)
    try {
      const filesIntent = await Promise.all(
        files().map(async file => {
          if (file.src) return { src: file.src, title: file.name }
          if (!file.file) return null

          const src = await uploadFile(file.file, ["user", userId()])
          return { src, title: file.name }
        }),
      )

      const response = await upsertForm({
        input: {
          formId: form()?.id ?? null,
          name: name(),
          content: normalizeTranslatableField({
            value: content(),
            defaultLocale: publicationLocale(),
            availableLocales: availableLocales(),
          }),
          emailSubject: emailSubject(),
          emailBody: normalizeTranslatableField({
            value: emailBody(),
            defaultLocale: publicationLocale(),
            availableLocales: availableLocales(),
          }),
          files: filesIntent.filter(
            (file): file is { src: string; title: string } => file !== null,
          ),
          private: audienceSettings().private,
        },
      })

      if (!response.success) return
      const id = response.data.id

      if (!id) return

      if (!isEditing()) {
        toast.success(LL().blog.formsPage.formCreated())
        navigate(paths().formsEdit(id), { replace: true })
        return
      }

      refetch()
      toast.success(LL().blog.formsPage.formUpdated())
    } catch (error) {
      const message =
        error instanceof Error && error.message.trim().length > 0
          ? error.message
          : LL().common.error()
      toast.error(message)
    } finally {
      setIsSaving(false)
    }
  }

  async function onUploadFiles(uploadedFiles: File[]) {
    if (uploadedFiles.length === 0) return
    setFiles(prev => [
      ...prev,
      ...uploadedFiles.map(file => ({
        id: crypto.randomUUID(),
        name: file.name,
        size: file.size,
        src: null,
        file,
      })),
    ])
  }

  function onRemoveFile(fileId: string) {
    setFiles(prev => prev.filter(file => file.id !== fileId))
  }

  function formatFileSize(bytes: number | null) {
    if (bytes === null) return "—"
    if (bytes < 1_024) return `${bytes} B`
    if (bytes < 1_024 * 1_024) return `${(bytes / 1_024).toFixed(1)} KB`
    return `${(bytes / (1_024 * 1_024)).toFixed(1)} MB`
  }
  const buttons = createMemo(() => [
    {
      label: LL().blog.title(),
      href: paths().url,
    } as const,
    {
      label: LL().blog.forms(),
      href: paths().forms(),
    } as const,
  ])

  return (
    <MainLayout
      hue="amber"
      header={
        <NavigationWithButtons
          hue="amber"
          title={
            isEditing()
              ? LL().blog.formsPage.editEditorTitle()
              : LL().blog.formsPage.editorTitle()
          }
          buttons={buttons()}
        >
          <Button
            hue="amber"
            onClick={onSave}
            disabled={!canSave()}
            active={isSaving()}
          >
            {isEditing()
              ? LL().common.save()
              : LL().blog.formsPage.createForm()}
          </Button>
        </NavigationWithButtons>
      }
    >
      <div class={formContainerClass}>
        <Show
          when={publication()}
          fallback={<Info>{LL().common.loading()}</Info>}
        >
          <Form
            title={
              isEditing()
                ? LL().blog.formsPage.editForm()
                : LL().blog.formsPage.formSectionTitle()
            }
            description={LL().blog.formsPage.formSectionDescription()}
            hue="amber"
            iconName="gift"
            onSubmit={onSave}
          >
            <TranslatableFieldGroup
              availableLocales={availableLocales()}
              defaultLocale={publicationLocale()}
            >
              <LanguageSelector />
              <TranslatableField
                value={name()}
                onChange={setName}
                availableLocales={availableLocales()}
                defaultLocale={publicationLocale()}
                name="leadMagnetFormName"
                format="text"
              >
                {(value, onChange) => (
                  <TextField
                    value={value()}
                    onChange={nextValue => onChange(nextValue ?? "")}
                  >
                    <TextField.Label>
                      {LL().blog.formsPage.name()}
                    </TextField.Label>
                    <TextField.Input />
                  </TextField>
                )}
              </TranslatableField>

              <TranslatableField
                value={content()}
                onChange={setContent}
                availableLocales={availableLocales()}
                defaultLocale={publicationLocale()}
                name="leadMagnetFormContent"
                format="tiptap"
              >
                {(value, onChange) => (
                  <SimpleWysiwyg
                    label={LL().blog.formsPage.content()}
                    content={value}
                    title={LL().blog.formsPage.content()}
                    publicationName={publication()?.name ?? ""}
                    onSave={json => {
                      onChange(JSON.stringify(json))
                    }}
                    blocks={{
                      image: {
                        uploadFolder: ["user", userId()],
                      },
                      horizontalRule: true,
                      codeBlock: true,
                      quote: true,
                    }}
                    dialogContent={<LanguageSelector />}
                  />
                )}
              </TranslatableField>
            </TranslatableFieldGroup>
          </Form>

          <Form
            title={LL().blog.formsPage.filesSectionTitle()}
            description={LL().blog.formsPage.filesSectionDescription()}
            hue="amber"
            iconName="paperclip"
            onSubmit={onSave}
          >
            <Box gap="16">
              <FileUpload
                hue="amber"
                text={LL().blog.formsPage.dropFiles()}
                onUpload={onUploadFiles}
                multiple
                disabled={isSaving()}
                hasFile={false}
              />

              <Show when={filesForTable().length > 0}>
                <div class={filesTableClass}>
                  <Table>
                    <Table.Head>
                      <Table.Header>{LL().blog.formsPage.name()}</Table.Header>
                      <Table.Header>{LL().blog.formsPage.size()}</Table.Header>
                      <Table.Header />
                    </Table.Head>
                    <Table.Body>
                      <For each={filesForTable()}>
                        {file => (
                          <Table.Row>
                            <Table.Cell>{file.name}</Table.Cell>
                            <Table.Cell>{formatFileSize(file.size)}</Table.Cell>
                            <Table.Cell>
                              <Button
                                hue="rose"
                                kind="ghost"
                                iconName="trash-2"
                                class={filesDeleteButtonClass}
                                onClick={() => onRemoveFile(file.id)}
                              />
                            </Table.Cell>
                          </Table.Row>
                        )}
                      </For>
                    </Table.Body>
                  </Table>
                </div>
              </Show>
            </Box>
          </Form>

          <Form
            title={LL().blog.formsPage.email()}
            description={LL().blog.formsPage.emailSectionDescription()}
            hue="amber"
            iconName="mail"
            onSubmit={onSave}
          >
            <TranslatableFieldGroup
              availableLocales={availableLocales()}
              defaultLocale={publicationLocale()}
            >
              <LanguageSelector />
              <TranslatableField
                value={emailSubject()}
                onChange={setEmailSubject}
                availableLocales={availableLocales()}
                defaultLocale={publicationLocale()}
                name="leadMagnetFormEmailSubject"
                format="text"
              >
                {(_value, _onChange) => null}
              </TranslatableField>
              <TranslatableField
                value={emailBody()}
                onChange={setEmailBody}
                availableLocales={availableLocales()}
                defaultLocale={publicationLocale()}
                name="leadMagnetFormEmailBody"
                format="tiptap"
              >
                {(value, onChange) => (
                  <SimpleWysiwyg
                    label={LL().blog.formsPage.emailBody()}
                    content={value}
                    title={LL().blog.formsPage.email()}
                    publicationName={publication()?.name ?? ""}
                    onSave={json => {
                      onChange(JSON.stringify(json))
                    }}
                    blocks={{
                      image: {
                        uploadFolder: ["user", userId()],
                      },
                      horizontalRule: true,
                      codeBlock: true,
                      quote: true,
                    }}
                    dialogContent={
                      <>
                        <LanguageSelector />
                        <TextField
                          value={emailSubject()}
                          onChange={nextValue =>
                            setEmailSubject(nextValue ?? "")
                          }
                        >
                          <TextField.Label>
                            {LL().blog.formsPage.emailSubject()}
                          </TextField.Label>
                          <TextField.Input />
                        </TextField>
                      </>
                    }
                  />
                )}
              </TranslatableField>
            </TranslatableFieldGroup>
          </Form>

          <Form
            title={LL().blog.formsPage.audience()}
            description={LL().blog.formsPage.audienceDescription()}
            hue="amber"
            iconName="user"
            onSubmit={onSave}
          >
            <AudienceForm
              copy="form"
              post={audiencePost()}
              onAudienceChange={settings => setAudienceSettings(settings)}
            />
          </Form>
        </Show>
      </div>
    </MainLayout>
  )
}

function createSimpleDoc(paragraphs: string[]): string {
  return JSON.stringify({
    type: "doc",
    content: paragraphs.map(text => ({
      type: "paragraph",
      content: [{ type: "text", text }],
    })),
  })
}

function getFileName(src: string): string {
  const withoutQuery = src.split("?")[0] ?? src
  const value = withoutQuery.split("/").at(-1)?.trim() ?? ""
  return value.length > 0 ? decodeURIComponent(value) : src
}

function normalizeTranslatableField(params: {
  value: string
  defaultLocale: Locale
  availableLocales: Locale[]
}) {
  const trimmed = params.value.trim()
  if (!trimmed) return null
  if (parseTranslatableField(trimmed)) return trimmed

  try {
    JSON.parse(trimmed)
    return buildTranslatableField(
      () => trimmed,
      params.defaultLocale,
      params.availableLocales,
    )
  } catch {
    return trimmed
  }
}
